import { FieldTypes } from '../../../constants/FieldTypes'
import { addFieldRow } from '../../../fields'
import {
  AppName,
  BooleanField,
  CheckboxField,
  DateField,
  DateTimeField,
  DropdownField,
  DropdownOption,
  FieldMap,
  ListField,
  MultipleSelectField,
  NumberField,
  TextAreaField,
  TextField,
} from '../../../types'
import {
  AboBloodType,
  AntepartumRisk,
  AntibodyScreenResult,
  BowelSymptom,
  Contractions,
  EddEstablishedMethod,
  EdemaGrade,
  EdemaLocation,
  FetalMovement,
  FetalPosition,
  FundalHeightLandmark,
  FundalHeightUnit,
  GbsStatus,
  Headache,
  LeakingFluidAmount,
  LeakingFluidColor,
  MdmComplexity,
  MdmDataReviewedItem,
  MdmProblemsAddressed,
  MdmRisk,
  NauseaVomiting,
  NstResult,
  NstTestType,
  PatientEducationTopic,
  PlanItemCategory,
  PregnancyConfirmationMethod,
  ReferralTarget,
  RhFactor,
  UrinarySymptom,
  VaginalBleedingColor,
  VisualChanges,
} from '../types/prenatal'
import { patientConcernsTextField, returnVisitIntervalField } from './shared'

// §2.4: Fundal height — two input modes. Landmark for early pregnancy; cm for ≥20w.
// Both always present; ExamVitalsInput emphasises the appropriate one based on GA.
export const fundalHeightLandmarkField: DropdownField<FundalHeightLandmark> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fundal Height (Landmark)',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'not-yet-palpable', text: 'Not Yet Palpable' },
    { id: 'at-pubic-symphysis', text: 'At Pubic Symphysis' },
    { id: 'above-pubic-symphysis', text: 'Above Pubic Symphysis' },
    { id: 'below-pubic-symphysis', text: 'Below Pubic Symphysis' },
    { id: 'midway-pubis-umbilicus', text: 'Midway — Pubis to Umbilicus' },
    { id: 'at-umbilicus', text: 'At Umbilicus' },
  ],
}

const fundalHeightMeasurementField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Fundal Height Measurement',
  condition: v => !!v?.fundalHeightLandmark && v.fundalHeightLandmark !== 'not-yet-palpable',
}

const fundalHeightUnitsField: DropdownField<FundalHeightUnit> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fundal Height Units',
  options: [
    { id: 'fingers-above-pubis', text: 'Fingers Above Pubis' },
    { id: 'fingers-below-umbilicus', text: 'Fingers Below Umbilicus' },
    { id: 'cm-from-pubis', text: 'cm from pubis' },
  ],
  condition: v => !!v?.fundalHeightLandmark && v.fundalHeightLandmark !== 'not-yet-palpable',
}

// §2.4: Hidden before 28w (196 days), Tier 2 at 28–35w, Tier 1 at 36w+ for routine.
// Visibility is gated by gestationalAge stored in form data.
const fetalPosition: DropdownField<FetalPosition> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fetal Position',
  condition: v => typeof v?.gestationalAge === 'number' && v.gestationalAge >= 196,
  options: [
    { id: 'cephalic', text: 'Cephalic' },
    { id: 'breech-frank', text: 'Breech — Frank' },
    { id: 'breech-complete', text: 'Breech — Complete' },
    { id: 'breech-footling', text: 'Breech — Footling' },
    { id: 'transverse', text: 'Transverse' },
    { id: 'oblique', text: 'Oblique' },
    { id: 'not-yet-clinically-relevant', text: 'Not Yet Clinically Relevant' },
  ],
  warningMessage: 'Fetal position should be documented at each antepartum visit after 28 weeks.',
}

const fetalHeartTones: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Fetal Heart Tones (bpm)',
  warningMessage: 'Fetal heart tones should be documented at each antepartum visit.',
}

const edemaGradeField: DropdownField<EdemaGrade> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Edema',
  inputType: 'radio',
  options: [
    { id: 'none', text: 'None' },
    { id: 'trace', text: 'Trace' },
    { id: '1+', text: '1+' },
    { id: '2+', text: '2+' },
    { id: '3+', text: '3+' },
    { id: '4+', text: '4+' },
  ],
  warningMessage: 'Edema assessment is a standard component of antepartum vital signs.',
}

const edemaLocationField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'Edema Location',
  optional: true,
  condition: v => !!v?.edema && v.edema !== 'none',
  options: [
    { id: 'ankles' as EdemaLocation, text: 'Ankles' },
    { id: 'hands' as EdemaLocation, text: 'Hands' },
    { id: 'face' as EdemaLocation, text: 'Face' },
  ],
}

const chiefComplaintField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Chief Complaint',
  hints: [
    {
      text: "What is the patient  presenting with today? e.g., 'Urinary burning and  frequency x 2 days.'",
    },
  ],
}

export const clinicalRelationshipToPregnancyField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Why is this condition relevant to this pregnancy?',
}

const fetalMovementField: DropdownField<FetalMovement> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Fetal Movement',
  inputType: 'radio',
  options: [
    { id: 'present', text: 'Present' },
    { id: 'decreased', text: 'Decreased' },
    { id: 'not-felt-yet', text: 'Not Felt Yet' },
  ],
}

const kickCountField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Kick Count',
  condition: v => v?.fetalMovement === 'decreased',
}

const contractionsField: DropdownField<Contractions> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Contractions',
  inputType: 'radio',
  options: [
    { id: 'none', text: 'None' },
    { id: 'irregular-bh', text: 'Irregular BH' },
    { id: 'regular', text: 'Regular' },
  ],
  warningMessage: 'Contraction status is a standard interval history item.',
}

const contractionFrequencyField: NumberField = {
  type: FieldTypes.NUMBER,
  condition: v => v?.contractions === 'regular',
  placeholder: 'Contraction Frequency',
  unit: 'min',
  min: 0,
  max: 30,
  step: 0.5,
  precision: 1,
}

const contractionDurationField: NumberField = {
  type: FieldTypes.NUMBER,
  condition: v => v?.contractions === 'regular',
  placeholder: 'Contraction Duration',
  unit: 'sec',
  min: 0,
  max: 300,
}

const vaginalBleedingField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Vaginal Bleeding or Spotting',
}

const vaginalBleedingAmountField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'Amount',
  unit: 'ml',
  min: 0,
  max: 100,
}

const vaginalBleedingColorField: DropdownField<VaginalBleedingColor> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Color',
  inputType: 'radio',
  options: [
    { id: 'red', text: 'Red' },
    { id: 'brown', text: 'Brown' },
  ],
}

const vaginalBleedingPrecipitatingFactorField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Precipitating Factor',
}

const vaginalBleedingDetailsField: FieldMap = {
  name: 'Vaginal Bleeding or Spotting Details',
  condition: v => v?.vaginalBleeding === true,
  children: {
    amount: addFieldRow(vaginalBleedingAmountField, { index: 0, flex: 1 }),
    color: addFieldRow(vaginalBleedingColorField, { index: 0, flex: 1 }),
    precipitatingFactor: addFieldRow(vaginalBleedingPrecipitatingFactorField, {
      index: 0,
      flex: 1,
    }),
  },
}

// §2.5: Tier 2 for routine visits (should not block a routine note); Tier 1 only
// on non-routine visits where fluid leaking is the presenting concern (handled via SOAP note).
const leakingFluidField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Leaking Fluid',
  optional: true,
  redFlagYesReason:
    'PROM protocol: assess with amniocheck/ferning, NST/BPP, and obstetric consultation as indicated',
}

const leakingFluidColorField: DropdownField<LeakingFluidColor> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Color',
  inputType: 'radio',
  options: [
    { id: 'clear', text: 'Clear' },
    { id: 'yellow', text: 'Yellow' },
    { id: 'green', text: 'Green / Meconium-stained' },
    { id: 'bloody', text: 'Bloody' },
  ],
}

const leakingFluidAmountField: DropdownField<LeakingFluidAmount> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Amount',
  inputType: 'radio',
  options: [
    { id: 'scant', text: 'Scant' },
    { id: 'moderate', text: 'Moderate' },
    { id: 'large', text: 'Large / Gush' },
  ],
}

const leakingFluidOnsetField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Onset',
}

const leakingFluidDetailsField: FieldMap = {
  name: 'Leaking Fluid Details',
  condition: v => v?.leakingFluid === true,
  children: {
    color: addFieldRow(leakingFluidColorField, { index: 0, flex: 1 }),
    amount: addFieldRow(leakingFluidAmountField, { index: 0, flex: 1 }),
    onset: addFieldRow(leakingFluidOnsetField, { index: 0, flex: 1 }),
  },
}

const headacheField: DropdownField<Headache> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Headache',
  inputType: 'radio',
  options: [
    { id: 'none', text: 'None' },
    { id: 'mild', text: 'Mild' },
    {
      id: 'severe',
      text: 'Severe',
      redFlagReason:
        'Severe headache with elevated BP may indicate preeclampsia — evaluate BP and symptoms',
    },
  ],
  warningMessage: 'Headache assessment helps evaluate for hypertensive disorders of pregnancy.',
}

const visualChangesField: DropdownField<VisualChanges> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Visual Changes',
  inputType: 'radio',
  options: [
    { id: 'none', text: 'None' },
    { id: 'blurring', text: 'Blurring' },
    { id: 'spots', text: 'Spots' },
    { id: 'flashing', text: 'Flashing Lights' },
  ],
  warningMessage: 'Visual changes may indicate hypertensive complications of pregnancy.',
}

const epigastricPainField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: 'Epigastric / RUQ Pain',
  redFlagYesReason:
    'May indicate preeclampsia or HELLP syndrome — evaluate BP, LFTs, and platelets',
  warningMessage: 'Epigastric or RUQ pain may indicate preeclampsia or HELLP syndrome.',
}

const epigastricPainNotesField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Describe pain',
}

const epigastricPainDetailsField: FieldMap = {
  name: '',
  condition: v => v?.epigastricPain === true,
  children: {
    notes: epigastricPainNotesField,
  },
}

// §2.5: Explicit None option required — empty field is ambiguous between "not asked" and "none reported".
const urinarySymptomsField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'Urinary Symptoms',
  hints: [
    { text: 'Select "None" if the patient reports no urinary symptoms.' },
    { text: 'If any symptoms present, consider ordering urinalysis / urine culture.' },
  ],
  options: [
    { id: 'none' as UrinarySymptom, text: 'None' },
    { id: 'burning' as UrinarySymptom, text: 'Burning' },
    { id: 'frequency' as UrinarySymptom, text: 'Frequency' },
    { id: 'urgency' as UrinarySymptom, text: 'Urgency' },
    { id: 'blood' as UrinarySymptom, text: 'Blood in Urine' },
  ],
  warningMessage: 'Urinary symptom review helps identify infection during pregnancy.',
}

const nauseaVomitingField: DropdownField<NauseaVomiting> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Nausea / Vomiting',
  inputType: 'radio',
  options: [
    { id: 'none', text: 'None' },
    { id: 'mild', text: 'Mild' },
    { id: 'moderate', text: 'Moderate' },
    { id: 'severe', text: 'Severe' },
  ],
}

const moodScaleField: DropdownField<1 | 2 | 3 | 4 | 5> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Mood / Emotional Well-being',
  inputType: 'radio',
  options: [
    { id: 1, text: '1' },
    { id: 2, text: '2' },
    { id: 3, text: '3' },
    { id: 4, text: '4' },
    { id: 5, text: '5' },
  ],
  hints: [
    {
      text: '1 = Very low, 5 = Very good. Score ≤ 2 → consider PHQ-9 or Edinburgh Postnatal Depression Scale',
    },
  ],
  warningMessage:
    'Mood and emotional well-being assessment is a standard component of antepartum care.',
}

const moodNotesField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Mood Notes',
  optional: true,
}

const nutritionHydrationNotesField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Nutrition / Hydration',
  withDotPhrases: true,
  hints: [{ text: 'e.g., fluid intake, prenatal vitamins, dietary concerns, food aversions' }],
}

const physicalActivityNotesField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Physical Activity',
  withDotPhrases: true,
  hints: [{ text: 'e.g., activity type and frequency, any restrictions or modifications' }],
}

// §2.5: Explicit None option required — same rationale as urinary symptoms.
const bowelGISymptomsField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'Bowel / GI Symptoms',
  hints: [{ text: 'Select "None" if the patient reports no GI symptoms.' }],
  options: [
    { id: 'none' as BowelSymptom, text: 'None' },
    { id: 'constipation' as BowelSymptom, text: 'Constipation' },
    { id: 'diarrhea' as BowelSymptom, text: 'Diarrhea' },
    { id: 'nausea' as BowelSymptom, text: 'Nausea' },
    { id: 'heartburn' as BowelSymptom, text: 'Heartburn / Reflux' },
    { id: 'other' as BowelSymptom, text: 'Other' },
  ],
}

// §2.6: General Illness Exam — collapsible sub-section for non-routine in-person visits. Tier 2.
export const generalIllnessExamField: FieldMap = {
  name: 'General Illness Exam',
  optional: true,
  children: {
    generalAppearance: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'General Appearance / Affect',
      optional: true,
    } as TextAreaField,
    throatOropharynx: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Throat / Oropharynx',
      optional: true,
    } as TextAreaField,
    lymphNodes: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Lymph Nodes',
      optional: true,
    } as TextAreaField,
    lungAuscultation: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Lung Auscultation',
      optional: true,
    } as TextAreaField,
    hydrationStatus: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Hydration Status',
      optional: true,
    } as TextAreaField,
    painScale: {
      type: FieldTypes.NUMBER,
      placeholder: 'Pain Scale (0–10)',
      min: 0,
      max: 10,
      optional: true,
    } as NumberField,
  },
}

// §2.1.4: SOAP note field for non-routine antepartum visits. All four fields are Tier 1.
// §B.1: assessment and plan have a minimum content-length check on top of presence validation.
export const soapNoteField: FieldMap = {
  name: 'SOAP Note',
  children: {
    subjective: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Subjective (S)',
      withDotPhrases: true,
      hints: [
        { text: "Chief complaint and history of present illness in the patient's own words." },
      ],
    } as TextAreaField,
    objective: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Objective (O)',
      withDotPhrases: true,
      hints: [{ text: 'Vital signs, physical exam findings, and relevant objective data.' }],
    } as TextAreaField,
    assessment: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Assessment (A)',
      withDotPhrases: true,
      hints: [{ text: 'Clinical assessment and diagnoses. Required for E&M documentation.' }],
      validate: value =>
        value && (value as string).trim().length > 10
          ? undefined
          : 'Please document your clinical assessment of this patient',
    } as TextAreaField,
    plan: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Plan (P)',
      withDotPhrases: true,
      hints: [
        {
          text: 'Management plan including orders, referrals, education, and follow-up. Required for E&M documentation.',
        },
      ],
      validate: value =>
        value && (value as string).trim().length > 10
          ? undefined
          : 'Please document the clinical management plan for this visit',
    } as TextAreaField,
  },
}

// §2.1 Non-Routine + Telehealth: Remote Assessment — all fields optional (patient-reported).
// Replaces General Illness Exam when non-routine visit uses a telehealth modality.
export const remoteAssessmentField: FieldMap = {
  name: 'Remote Assessment',
  children: {
    generalAppearance: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'General Appearance (Video Observation)',
      hints: [{ text: 'Provider observes via video. Free text.' }],
      optional: true,
    } as TextAreaField,
    patientReportedBp: {
      type: FieldTypes.TEXT,
      placeholder: 'Patient-Reported Blood Pressure',
      hints: [{ text: 'If home cuff available.' }],
      optional: true,
    } as TextField,
    patientReportedWeight: {
      type: FieldTypes.TEXT,
      placeholder: 'Patient-Reported Weight',
      hints: [{ text: 'If home scale available.' }],
      optional: true,
    } as TextField,
    patientReportedTemp: {
      type: FieldTypes.TEXT,
      placeholder: 'Patient-Reported Temperature',
      hints: [{ text: 'If home thermometer available.' }],
      optional: true,
    } as TextField,
    patientReportedO2Sat: {
      type: FieldTypes.TEXT,
      placeholder: 'Patient-Reported O2 Saturation',
      hints: [{ text: 'If home pulse oximeter available.' }],
      optional: true,
    } as TextField,
    fetalMovement: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Fetal Movement',
      hints: [{ text: 'Patient reports.' }],
      optional: true,
    } as TextAreaField,
    breathingRespiratoryEffort: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Breathing / Respiratory Effort',
      hints: [{ text: 'Observable via video or reported.' }],
      optional: true,
    } as TextAreaField,
    affectAndMood: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Affect and Mood',
      hints: [{ text: 'Observable via video or reported.' }],
      optional: true,
    } as TextAreaField,
    chiefComplaintDetails: {
      type: FieldTypes.TEXTAREA,
      placeholder: 'Chief Complaint Details',
      hints: [{ text: 'Patient describes symptoms.' }],
      optional: true,
    } as TextAreaField,
  },
}

// §2.7: External records reviewed — Yes/No toggle; triggers external records time field in Time & Work Log.
export const externalRecordsReviewedField: FieldMap = {
  name: 'External Records',
  children: {
    externalRecordsReviewed: {
      type: FieldTypes.BOOLEAN,
      inputType: 'radio',
      placeholder: 'Did you review any external records today?',
      optional: true,
    } as BooleanField,
    externalRecordsReviewedText: {
      type: FieldTypes.TEXT,
      placeholder: 'What did you review?',
      optional: true,
      condition: v => v?.externalRecordsReviewed === true,
    } as TextField,
  },
}

export const chiefComplaintFieldMap: FieldMap = {
  name: 'Chief Complaint',
  children: {
    chiefComplaint: chiefComplaintField,
  },
}

export const nonRoutinePrenatalExamField: FieldMap = {
  name: 'Clinical Rationale',
  children: {
    clinicalRelationshipToPregnancy: clinicalRelationshipToPregnancyField,
  },
}

export const prenatalExamField: FieldMap = {
  name: 'Prenatal Visit',
  children: {
    fundalHeightLandmark: fundalHeightLandmarkField,
    fundalHeightMeasurement: addFieldRow(fundalHeightMeasurementField, { index: 0, flex: 1 }),
    fundalHeightUnits: addFieldRow(fundalHeightUnitsField, { index: 0, flex: 1 }),
    fetalPosition: addFieldRow(fetalPosition, { index: 1, flex: 1 }),
    fetalHeartTones: addFieldRow(fetalHeartTones, { index: 1, flex: 1 }),
    edema: edemaGradeField,
    edemaLocation: edemaLocationField,
    fetalMovement: fetalMovementField,
    kickCount: kickCountField,
    contractions: contractionsField,
    contractionFrequency: addFieldRow(contractionFrequencyField, { index: 2, flex: 1 }),
    contractionDuration: addFieldRow(contractionDurationField, { index: 2, flex: 1 }),
    vaginalBleeding: vaginalBleedingField,
    vaginalBleedingDetails: vaginalBleedingDetailsField,
    leakingFluid: leakingFluidField,
    leakingFluidDetails: leakingFluidDetailsField,
    headache: headacheField,
    visualChanges: visualChangesField,
    epigastricPain: epigastricPainField,
    epigastricPainDetails: epigastricPainDetailsField,
    urinarySymptoms: urinarySymptomsField,
    nauseaVomiting: nauseaVomitingField,
    nutritionHydration: nutritionHydrationNotesField,
    physicalActivity: physicalActivityNotesField,
    bowelGISymptoms: bowelGISymptomsField,
    moodScale: moodScaleField,
    moodNotes: moodNotesField,
    patientConcerns: patientConcernsTextField,
  },
}

export const firstObVisitField: FieldMap = {
  name: 'First OB Visit',
  optional: true,
  children: {
    isFirstObVisit: {
      type: FieldTypes.CHECKBOX,
      placeholder: 'Confirmation of pregnancy visit?',
      optional: true,
    } as CheckboxField,
    pregnancyConfirmationMethod: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Pregnancy Confirmation Method',
      optional: true,
      options: [
        { id: 'urine-hcg', text: 'Urine HCG' },
        { id: 'serum-hcg', text: 'Serum HCG' },
        { id: 'ultrasound', text: 'Ultrasound' },
        { id: 'clinical-exam', text: 'Clinical Exam' },
      ],
      condition: v => !!v?.isFirstObVisit,
    } as DropdownField<PregnancyConfirmationMethod>,
    pregnancyConfirmationDetail: {
      type: FieldTypes.TEXT,
      placeholder: 'Confirmation Details',
      optional: true,
      condition: v => !!v?.isFirstObVisit,
    } as TextField,
    eddEstablishedMethod: {
      type: FieldTypes.DROPDOWN,
      inputType: 'radio',
      placeholder: 'EDD Established Method',
      optional: true,
      options: [
        { id: 'lmp', text: 'LMP' },
        { id: 'ultrasound', text: 'Ultrasound' },
        { id: 'clinical-estimate', text: 'Clinical Estimate' },
      ],
      condition: v => !!v?.isFirstObVisit,
    } as DropdownField<EddEstablishedMethod>,
    eddUltrasoundDate: {
      type: FieldTypes.DATE,
      placeholder: 'Ultrasound Date',
      optional: true,
      condition: v => !!v?.isFirstObVisit && v?.eddEstablishedMethod === 'ultrasound',
    } as DateField,
    eddUltrasoundGa: {
      type: FieldTypes.TEXT,
      placeholder: 'GA at Time of Scan (e.g., 12w 3d)',
      optional: true,
      condition: v => !!v?.isFirstObVisit && v?.eddEstablishedMethod === 'ultrasound',
    } as TextField,
    patientHistoryReviewConfirmed: {
      type: FieldTypes.CHECKBOX,
      placeholder: 'Patient-submitted history reviewed and discussed',
      optional: true,
      condition: v => !!v?.isFirstObVisit,
    } as CheckboxField,
    patientHistoryReviewTimestamp: {
      type: FieldTypes.DATETIME,
      placeholder: 'Review Timestamp',
      optional: true,
      defaultToNow: true,
      condition: v => !!v?.isFirstObVisit && !!v?.patientHistoryReviewConfirmed,
    } as DateTimeField,
  },
}

// §2.8: Assessment & Plan fields

// §B.1: Minimum content-length check — presence alone is insufficient for E&M documentation.
// Error message is clinically framed (no billing language).
export const assessmentField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Assessment',
  withDotPhrases: true,
  hints: [{ text: 'Document your clinical assessment of this patient today.' }],
  validate: value =>
    value && (value as string).trim().length > 10
      ? undefined
      : 'Please document your clinical assessment of this patient',
}

const planItemCategoryField: DropdownField<PlanItemCategory> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Category',
  inputType: 'native',
  options: [
    { id: 'labs', text: 'Labs' },
    { id: 'referral', text: 'Referral' },
    { id: 'education', text: 'Education' },
    { id: 'return-visit', text: 'Return Visit' },
    { id: 'rx', text: 'Prescription' },
    { id: 'procedure', text: 'Procedure' },
  ],
}

const planItemTextField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Plan detail',
  withDotPhrases: true,
}

const planItemFollowUpDateField: DateField = {
  type: FieldTypes.DATE,
  placeholder: 'Follow-up date',
  optional: true,
  isoFormat: true,
}

export const planItemsListField: ListField = {
  name: 'Plan Items',
  itemName: 'Plan Item',
  optional: true,
  itemFields: {
    name: 'Plan Item',
    children: {
      category: planItemCategoryField,
      text: planItemTextField,
      followUpDate: planItemFollowUpDateField,
    },
  },
}

export const referralsField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'Referrals',
  optional: true,
  options: [
    { id: 'mfm' as ReferralTarget, text: 'Maternal-Fetal Medicine (MFM)' },
    { id: 'ob' as ReferralTarget, text: 'OB / Obstetrics' },
    { id: 'cardiology' as ReferralTarget, text: 'Cardiology' },
    { id: 'nutrition' as ReferralTarget, text: 'Nutrition / Dietitian' },
    { id: 'social-work' as ReferralTarget, text: 'Social Work' },
    { id: 'mental-health' as ReferralTarget, text: 'Mental Health / Behavioral Health' },
    { id: 'other' as ReferralTarget, text: 'Other (specify in plan)' },
  ],
}

export const patientEducationField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'Patient Education Provided',
  optional: true,
  options: [
    { id: 'birth-plan' as PatientEducationTopic, text: 'Birth plan discussion' },
    { id: 'signs-of-labor' as PatientEducationTopic, text: 'Signs of labor' },
    { id: 'kick-counts' as PatientEducationTopic, text: 'Kick counts / fetal movement' },
    { id: 'gbs-results' as PatientEducationTopic, text: 'GBS results reviewed' },
    { id: 'nutrition' as PatientEducationTopic, text: 'Nutrition and hydration' },
    { id: 'breastfeeding' as PatientEducationTopic, text: 'Breastfeeding / infant feeding' },
  ],
}

export const antepartumRiskField: DropdownField<AntepartumRisk> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Risk Stratification',
  inputType: 'radio',
  options: [
    { id: 'low', text: 'Low' },
    { id: 'moderate', text: 'Moderate' },
    { id: 'high', text: 'High' },
  ],
}

export const antepartumRiskReasonField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Reason for elevated risk',
  optional: true,
  condition: v => v?.antepartumRisk === 'moderate' || v?.antepartumRisk === 'high',
}

// §2.5: Tier 1 confirmation field — provider checked that medications/supplements are current.
// Stored in encounter data; validated by 'medications-supplements' section type.
export const medicationsReviewedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Medications and supplements reviewed — no changes since last visit',
}

// §2.10.1: MDM field definitions and pure computation functions.

const selfLimitedTextAdmin = 'Self-limited or minor problem'
const selfLimitedTextProvider = 'Routine/stable - no acute problems addressed'
const selfLimitedOptionAdmin: DropdownOption<MdmProblemsAddressed> = {
  id: 'self-limited',
  text: selfLimitedTextAdmin,
}
const selfLimitedOptionProvider: DropdownOption<MdmProblemsAddressed> = {
  id: 'self-limited',
  text: selfLimitedTextProvider,
}

const stableChronicOption: DropdownOption<MdmProblemsAddressed> = {
  id: 'stable-chronic',
  text: 'Stable chronic illness',
}
const undiagnosedNewOption: DropdownOption<MdmProblemsAddressed> = {
  id: 'undiagnosed-new',
  text: 'Undiagnosed new problem requiring additional work-up',
}
const acuteSystemicOption: DropdownOption<MdmProblemsAddressed> = {
  id: 'acute-systemic',
  text: 'Acute illness with systemic symptoms',
}
const chronicSevereOption: DropdownOption<MdmProblemsAddressed> = {
  id: 'chronic-severe',
  text: 'Chronic illness with severe exacerbation',
}
const threatToLifeOption: DropdownOption<MdmProblemsAddressed> = {
  id: 'threat-to-life',
  text: 'Acute or chronic illness posing threat to life or function',
}

export const mdmProblemsAddressedFieldAdmin: DropdownField<MdmProblemsAddressed> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Problems Addressed',
  inputType: 'radio',
  options: [
    selfLimitedOptionAdmin,
    stableChronicOption,
    undiagnosedNewOption,
    acuteSystemicOption,
    chronicSevereOption,
    threatToLifeOption,
  ],
}

export const mdmProblemsAddressedFieldProvider: DropdownField<MdmProblemsAddressed> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Problems Addressed',
  inputType: 'radio',
  options: [
    selfLimitedOptionProvider,
    stableChronicOption,
    undiagnosedNewOption,
    acuteSystemicOption,
    chronicSevereOption,
    threatToLifeOption,
  ],
}

export const mdmDataReviewedField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'Data Reviewed',
  optional: true,
  options: [
    { id: 'labs-reviewed' as MdmDataReviewedItem, text: 'Lab results reviewed' },
    { id: 'external-records' as MdmDataReviewedItem, text: 'External records reviewed' },
    {
      id: 'independent-test-interpretation' as MdmDataReviewedItem,
      text: 'Independent interpretation of test results',
    },
    {
      id: 'provider-consultation' as MdmDataReviewedItem,
      text: 'Discussion with consulting provider',
    },
    {
      id: 'independent-historian' as MdmDataReviewedItem,
      text: 'History obtained from independent historian',
    },
  ],
}

export const mdmRiskOfManagementField: DropdownField<MdmRisk> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Risk of Management',
  inputType: 'radio',
  options: [
    { id: 'minimal', text: 'Minimal — no prescription drug management' },
    { id: 'low', text: 'Low — over-the-counter medications or minor procedure' },
    {
      id: 'moderate',
      text: 'Moderate — prescription drug management, diagnostic procedure with risk, or referral',
    },
    {
      id: 'high',
      text: 'High — hospitalization considered, major procedure, or life-threatening condition',
    },
  ],
}

const problemsRank: Record<MdmProblemsAddressed, number> = {
  'self-limited': 0,
  'stable-chronic': 1,
  'undiagnosed-new': 2,
  'acute-systemic': 2,
  'chronic-severe': 3,
  'threat-to-life': 3,
}
const riskRank: Record<MdmRisk, number> = { minimal: 0, low: 1, moderate: 2, high: 3 }
const rankToComplexity: MdmComplexity[] = ['straightforward', 'low', 'moderate', 'high']

export const computeMdmComplexity = (
  problems: MdmProblemsAddressed | undefined,
  dataReviewed: MdmDataReviewedItem[] | undefined,
  risk: MdmRisk | undefined,
): MdmComplexity | undefined => {
  if (!problems && !risk) return undefined
  const e1 = problems != null ? problemsRank[problems] : 0
  const itemCount = dataReviewed?.length ?? 0
  const e2 = itemCount >= 3 ? 2 : itemCount === 2 ? 1 : 0
  const e3 = risk != null ? riskRank[risk] : 0
  return rankToComplexity[Math.max(e1, e2, e3)]
}

const mdmProblemsLabelAdmin: Record<MdmProblemsAddressed, string> = {
  'self-limited': 'a self-limited or minor problem',
  'stable-chronic': 'a stable chronic illness',
  'undiagnosed-new': 'an undiagnosed new problem requiring additional work-up',
  'acute-systemic': 'an acute illness with systemic symptoms',
  'chronic-severe': 'a chronic illness with severe exacerbation',
  'threat-to-life': 'an acute or chronic illness posing threat to life or function',
}

const mdmProblemsLabelProvider: Record<MdmProblemsAddressed, string> = {
  'self-limited': 'a routine/stable problem with no acute issues addressed',
  'stable-chronic': 'a stable chronic illness',
  'undiagnosed-new': 'an undiagnosed new problem requiring additional work-up',
  'acute-systemic': 'an acute illness with systemic symptoms',
  'chronic-severe': 'a chronic illness with severe exacerbation',
  'threat-to-life': 'an acute or chronic illness posing threat to life or function',
}

const mdmDataLabel: Record<MdmDataReviewedItem, string> = {
  'labs-reviewed': 'laboratory results',
  'external-records': 'external records',
  'independent-test-interpretation': 'independent interpretation of test results',
  'provider-consultation': 'discussion with a consulting provider',
  'independent-historian': 'history from an independent historian',
}
const mdmRiskLabel: Record<MdmRisk, string> = {
  minimal: 'no prescription drug management',
  low: 'over-the-counter medications or minor procedure',
  moderate: 'prescription drug management',
  high: 'hospitalization or major procedures considered',
}

export const generateMdmSummary = (
  problems: MdmProblemsAddressed | undefined,
  dataReviewed: MdmDataReviewedItem[] | undefined,
  risk: MdmRisk | undefined,
  appName: AppName,
): string => {
  const parts: string[] = []
  if (problems)
    parts.push(
      `management of ${appName === 'app' ? mdmProblemsLabelAdmin[problems] : mdmProblemsLabelProvider[problems]}`,
    )
  if (dataReviewed?.length) parts.push(...dataReviewed.map(d => `review of ${mdmDataLabel[d]}`))
  if (risk) parts.push(mdmRiskLabel[risk])
  if (!parts.length) return ''
  const last = parts[parts.length - 1]
  const rest = parts.slice(0, -1)
  const joined = rest.length ? `${rest.join(', ')}, and ${last}` : last
  return `Assessment involved ${joined}.`
}

// §2.7: Labs & Diagnostics field definitions.
// labsDiagnosticsField is rendered at name="data.labs" — condition functions receive data.labs as v.

const labOrderedEntryField: FieldMap = {
  name: 'Lab',
  children: {
    labName: {
      type: FieldTypes.TEXT,
      placeholder: 'Lab name',
    } as TextField,
    clinicalIndication: {
      type: FieldTypes.TEXT,
      placeholder: 'Clinical indication',
    } as TextField,
  },
}

const labsOrderedThisVisitField: ListField = {
  name: 'Labs Ordered This Visit',
  itemName: 'Lab',
  optional: true,
  itemFields: labOrderedEntryField,
}

const labReviewConfirmationField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Lab results reviewed and acknowledged',
  optional: true,
}

// §2.7: GBS status — Tier 1 at 36–37w GA. GA-based visibility handled in LabsInput component.
const gbsStatusField: DropdownField<GbsStatus> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'GBS Status',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'negative', text: 'Negative' },
    { id: 'positive', text: 'Positive' },
    { id: 'unknown', text: 'Unknown / Not Yet Resulted' },
  ],
}

const gbsDateField: DateField = {
  type: FieldTypes.DATE,
  placeholder: 'GBS Collection Date',
  optional: true,
  isoFormat: true,
  // v = data.labs
  condition: v => !!v?.gbsStatus,
}

const gctValueField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: '1-hr GCT (mg/dL)',
  optional: true,
  min: 0,
  max: 600,
}

const gttOrderedField: BooleanField = {
  type: FieldTypes.BOOLEAN,
  inputType: 'radio',
  placeholder: '3-hr GTT ordered',
  optional: true,
  // v = data.labs
  condition: v => typeof v?.gctValueMgdl === 'number' && v.gctValueMgdl >= 130,
}

const gttResultField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'GTT Result',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'pass', text: 'Pass' },
    { id: 'fail', text: 'Fail' },
  ],
  // v = data.labs
  condition: v => !!v?.gttOrdered,
}

const gdmDiagnosedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'GDM Diagnosis confirmed',
  optional: true,
  // v = data.labs
  condition: v => v?.gttResult === 'fail',
}

const otherLabFlagStatusField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Flag status',
  optional: true,
  inputType: 'native',
  options: [
    { id: 'normal', text: 'Normal' },
    { id: 'high', text: 'High' },
    { id: 'low', text: 'Low' },
    { id: 'abnormal', text: 'Abnormal' },
  ],
}

const otherLabEntryField: FieldMap = {
  name: 'Lab',
  children: {
    labName: {
      type: FieldTypes.TEXT,
      placeholder: 'Lab name',
    } as TextField,
    result: {
      type: FieldTypes.TEXT,
      placeholder: 'Result',
      optional: true,
    } as TextField,
    flagStatus: otherLabFlagStatusField,
  },
}

const otherLabsField: ListField = {
  name: 'Other Labs',
  itemName: 'Lab',
  optional: true,
  itemFields: otherLabEntryField,
}

// §2.7.1: Blood type & Rh-negative protocol field definitions.
// All rendered at name="data.labs"; condition closures receive data.labs as v.

const bloodTypeAboField: DropdownField<AboBloodType> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Blood Type (ABO)',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'O', text: 'O' },
    { id: 'A', text: 'A' },
    { id: 'B', text: 'B' },
    { id: 'AB', text: 'AB' },
  ],
}

const bloodTypeRhField: DropdownField<RhFactor> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Rh Factor',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'positive', text: 'Positive (+)' },
    { id: 'negative', text: 'Negative (−)' },
  ],
}

const antibodyScreenOrderedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Rh antibody screen ordered',
  optional: true,
}

const antibodyScreenDateField: DateField = {
  type: FieldTypes.DATE,
  placeholder: 'Antibody Screen Date',
  optional: true,
  isoFormat: true,
  // v = data.labs
  condition: v => !!v?.antibodyScreenOrdered,
}

const antibodyScreenResultField: DropdownField<AntibodyScreenResult> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Antibody Screen Result',
  inputType: 'radio',
  optional: true,
  // v = data.labs
  condition: v => !!v?.antibodyScreenOrdered,
  options: [
    { id: 'negative', text: 'Negative' },
    { id: 'positive', text: 'Positive' },
    { id: 'weakly-positive', text: 'Weakly Positive' },
  ],
}

const rhogamDateField: DateField = {
  type: FieldTypes.DATE,
  placeholder: 'RhoGAM Date',
  optional: true,
  isoFormat: true,
}

const rhogamDoseField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Dose',
  optional: true,
  options: [
    { id: '300 mcg', text: '300 mcg (standard)' },
    { id: '50 mcg', text: '50 mcg (mini-dose)' },
  ],
}

const rhogamLotNumberField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Lot Number',
  optional: true,
}

const rhogamRouteField: DropdownField = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Route',
  optional: true,
  options: [
    { id: 'IM', text: 'IM (intramuscular)' },
    { id: 'IV', text: 'IV (intravenous)' },
  ],
}

const rhogamConsentObtainedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'Patient consent obtained',
  optional: true,
}

const rhogamAdministrationField: FieldMap = {
  name: 'RhoGAM Administration',
  children: {
    date: rhogamDateField,
    dose: rhogamDoseField,
    lotNumber: rhogamLotNumberField,
    route: rhogamRouteField,
    consentObtained: rhogamConsentObtainedField,
  },
}

const rhogamPostDeliveryIndicatedField: CheckboxField = {
  type: FieldTypes.CHECKBOX,
  placeholder: 'RhoGAM indicated post-delivery',
  optional: true,
}

// Sub-group FieldMaps — each rendered at name="data.labs" in LabsInput.
// Children are flat keys of LabsData, so paths resolve to data.labs.* correctly.
export const labsOrderedSectionField: FieldMap = {
  name: 'Labs Ordered This Visit',
  children: { labsOrderedThisVisit: labsOrderedThisVisitField },
}

export const labsGbsSectionField: FieldMap = {
  name: 'GBS Culture (36–37w)',
  children: {
    gbsStatus: gbsStatusField,
    gbsDate: gbsDateField,
  },
}

export const labsGlucoseSectionField: FieldMap = {
  name: 'Glucose Screen',
  children: {
    gctValueMgdl: gctValueField,
    gttOrdered: gttOrderedField,
    gttResult: gttResultField,
    gdmDiagnosed: gdmDiagnosedField,
  },
}

export const labsOtherSectionField: FieldMap = {
  name: 'Other Labs',
  children: { otherLabs: otherLabsField },
}

// §2.7: NST / BPP result entry — rendered at name="data.labs"
const nstBppTestTypeField: DropdownField<NstTestType> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'Test Type',
  inputType: 'radio',
  optional: true,
  options: [
    { id: 'nst', text: 'NST (Non-Stress Test)' },
    { id: 'modified-bpp', text: 'Modified BPP (NST + AFI)' },
    { id: 'full-bpp', text: 'Full BPP' },
  ],
}

const nstBppResultField: DropdownField<NstResult> = {
  type: FieldTypes.DROPDOWN,
  placeholder: 'NST Result',
  inputType: 'radio',
  optional: true,
  condition: v => v?.testType === 'nst' || v?.testType === 'modified-bpp',
  options: [
    { id: 'reactive', text: 'Reactive' },
    { id: 'non-reactive', text: 'Non-Reactive' },
    { id: 'equivocal', text: 'Equivocal' },
  ],
}

const nstBppScoreField: NumberField = {
  type: FieldTypes.NUMBER,
  placeholder: 'BPP Score (0–10)',
  optional: true,
  condition: v => v?.testType === 'modified-bpp' || v?.testType === 'full-bpp',
  min: 0,
  max: 10,
}

const nstBppTestDateField: DateField = {
  type: FieldTypes.DATE,
  placeholder: 'Test Date',
  optional: true,
}

const nstBppPerformedByField: TextField = {
  type: FieldTypes.TEXT,
  placeholder: 'Performed / Interpreted By',
  optional: true,
}

const nstBppNotesField: TextAreaField = {
  type: FieldTypes.TEXTAREA,
  placeholder: 'Notes',
  optional: true,
}

const nstBppEntryField: FieldMap = {
  name: 'NST / BPP Entry',
  children: {
    testType: nstBppTestTypeField,
    testDate: nstBppTestDateField,
    result: nstBppResultField,
    bppScore: nstBppScoreField,
    performedBy: nstBppPerformedByField,
    notes: nstBppNotesField,
  },
}

export const labsNstBppSectionField: FieldMap = {
  name: 'NST / BPP',
  children: { nstBpp: nstBppEntryField },
}

// §2.7.1: Blood type, antibody screen, and RhoGAM post-delivery flag — rendered at name="data.labs"
export const labsBloodTypeSectionField: FieldMap = {
  name: 'Blood Type & Rh Status',
  children: {
    bloodTypeAbo: bloodTypeAboField,
    bloodTypeRh: bloodTypeRhField,
    antibodyScreenOrdered: antibodyScreenOrderedField,
    antibodyScreenDate: antibodyScreenDateField,
    antibodyScreenResult: antibodyScreenResultField,
    rhogamPostDeliveryIndicated: rhogamPostDeliveryIndicatedField,
  },
}

// §2.7.1: RhoGAM administration record — rendered at name="data.labs" when Rh-negative
export const labsRhogamSectionField: FieldMap = {
  name: 'RhoGAM Administration',
  children: { rhogamAdministration: rhogamAdministrationField },
}

// Full field map — used for validation (getSectionValidationField) only.
// Do NOT render this via a single FormElement — use the sub-group exports above in LabsInput.
export const labsDiagnosticsField: FieldMap = {
  name: 'Labs & Diagnostics',
  children: {
    labsOrderedThisVisit: labsOrderedThisVisitField,
    labReviewConfirmation: labReviewConfirmationField,
    gbsStatus: gbsStatusField,
    gbsDate: gbsDateField,
    gctValueMgdl: gctValueField,
    gttOrdered: gttOrderedField,
    gttResult: gttResultField,
    gdmDiagnosed: gdmDiagnosedField,
    otherLabs: otherLabsField,
    bloodTypeAbo: bloodTypeAboField,
    bloodTypeRh: bloodTypeRhField,
    antibodyScreenOrdered: antibodyScreenOrderedField,
    antibodyScreenDate: antibodyScreenDateField,
    antibodyScreenResult: antibodyScreenResultField,
    rhogamAdministration: rhogamAdministrationField,
    rhogamPostDeliveryIndicated: rhogamPostDeliveryIndicatedField,
    nstBpp: nstBppEntryField,
  },
}

// §2.16: Pre-Visit Contact — optional section at top of antepartum step 1.
// contactMinutes becomes Tier 1 if contactType is set (enforced via EncounterFieldValidator).
export const preVisitContactField: FieldMap = {
  name: 'Pre-Visit Contact',
  children: {
    contactType: {
      type: FieldTypes.DROPDOWN,
      placeholder: 'Contact Type',
      inputType: 'radio',
      optional: true,
      options: [
        { id: 'phone-call', text: 'Phone call' },
        { id: 'video-call', text: 'Video call' },
        { id: 'in-person-only', text: 'In-person only (no pre-visit contact)' },
      ],
    } as DropdownField,
    contactMinutes: {
      type: FieldTypes.NUMBER,
      placeholder: 'Contact time (minutes)',
      optional: true,
    } as NumberField,
    contactNote: {
      type: FieldTypes.TEXT,
      placeholder: 'Brief note',
      optional: true,
    } as TextField,
  },
}

// §2.8: Full Assessment & Plan FieldMap — rendered at name="data" in AssessmentPlanInput.
// assessment and antepartumRisk are Tier 1. returnVisitInterval is Tier 2. All others optional.
export const assessmentPlanField: FieldMap = {
  name: 'Assessment & Plan',
  children: {
    assessment: assessmentField,
    planItems: planItemsListField,
    referrals: referralsField,
    patientEducation: patientEducationField,
    antepartumRisk: antepartumRiskField,
    antepartumRiskReason: antepartumRiskReasonField,
    returnVisitInterval: returnVisitIntervalField,
  },
}
